import { DmTaiLieuModel } from './../models/dmtailieu.model';
import { BaseService } from './base.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from './config.service';
import { ImportNhanVienModel, NhanVienModel, NhanVienSearch, NhanVienUpdateModel } from './../models/nhanvien.model';
import { ResponseData, PagedList } from './../models/model';
import { catchError } from 'rxjs/operators';
import { environment } from './../../environments/environment';
import { NhanVienShortModel, NhanVienWarningModel } from './../models/nhanvienshort.model';
import { SummaryModel } from '../models/dmluong.model';

@Injectable()
export class NhanVienService extends BaseService {

  private baseUrl;

  constructor(private http: HttpClient, private configService: ConfigService) {
    super();
    this.baseUrl = this.configService.getBaseUrl();
  }

  create(model: NhanVienModel): Observable<any> {
    const headers = this.getHeaders(true);
    return this.http.post<NhanVienModel>(this.baseUrl + this.configService.url.nhanvien.create, model, { headers })
      .pipe(catchError(this.handleError('createNhanVien', new ResponseData<any>())));
  }

  uploadImage(model: any): Observable<any> {
    const headers = this.getHeaders(true);
    return this.http.post<any>(this.baseUrl + this.configService.url.nhanvien.uploadImage, model, { headers });
  }

  getImage(fileUrl: string): Observable<any> {
    return this.http.get(this.baseUrl + this.configService.url.nhanvien.getImage + '/' + fileUrl, { observe: 'response', responseType: 'arraybuffer' });
  }

  update(model: NhanVienModel): Observable<any> {
    const headers = this.getHeaders(true);
    return this.http.put<NhanVienModel>(this.baseUrl + this.configService.url.nhanvien.update, model, { headers })
      .pipe(catchError(this.handleError('updateNhanVien', new ResponseData<any>())));
  }

  updateTrangThai(model: NhanVienUpdateModel): Observable<any> {
    const headers = this.getHeaders(true);
    return this.http.post<NhanVienUpdateModel>(this.baseUrl + this.configService.url.nhanvien.updateTrangThai, model, { headers })
      .pipe(catchError(this.handleError('updateNhanVienTrangThai', new ResponseData<any>())));
  }

  delete(ids: Array<number>): Observable<ResponseData<any>> {
    const headers = this.getHeaders(true);
    return this.http.delete<ResponseData<any>>(this.baseUrl + this.configService.url.nhanvien.delete + '/' + ids, { headers })
      .pipe(catchError(this.handleError('deleteNhanVien', new ResponseData<any>())));
  }

  getPagedList(search: NhanVienSearch): Observable<PagedList<NhanVienModel>> {
    const headers = this.getHeaders(true);
    return this.http.post<PagedList<NhanVienModel>>(this.baseUrl + this.configService.url.nhanvien.getPagedList, search, { headers })
      .pipe(catchError(this.handleError('find', new PagedList<NhanVienModel>())));
  }

  getDataListWarning(search: NhanVienSearch): Observable<PagedList<NhanVienModel>> {
    const headers = this.getHeaders(true);
    return this.http.post<PagedList<NhanVienModel>>(this.baseUrl + this.configService.url.nhanvien.getPagedListWarning, search, { headers })
      .pipe(catchError(this.handleError('find', new PagedList<NhanVienModel>())));
  }

  getById(id: number): Observable<NhanVienModel> {
    const headers = this.getHeaders(true);
    return this.http.get<NhanVienModel>(this.baseUrl + this.configService.url.nhanvien.getById + '/' + id, { headers })
      .pipe(catchError(this.handleError('getNhanVienById', new NhanVienModel())));
  }

  getDetail(): Observable<NhanVienModel> {
    const headers = this.getHeaders(true);
    return this.http.get<NhanVienModel>(this.baseUrl + this.configService.url.nhanvien.detail, { headers })
      .pipe(catchError(this.handleError('getDetailNhanVien', new NhanVienModel())));
  }

  getView(id: number): Observable<NhanVienModel> {
    const headers = this.getHeaders(true);
    return this.http.get<NhanVienModel>(this.baseUrl + this.configService.url.nhanvien.getView + '/' + id, { headers })
      .pipe(catchError(this.handleError('getNhanVienById', new NhanVienModel())));
  }

  upload(dmTaiLieuModel: DmTaiLieuModel): Observable<any> {
    const formData = new FormData();
    formData.append('file', dmTaiLieuModel.file);
    return this.http.post<any>(this.baseUrl + this.configService.url.nhanvien.upload, formData, { observe: 'response' });
  }

  export(search: NhanVienSearch): Observable<any> {
    return this.http.post(this.baseUrl + this.configService.url.nhanvien.export, search, { observe: 'response', responseType: 'arraybuffer' });
  }

  exportBySheet(search: NhanVienSearch): Observable<any> {
    return this.http.post(this.baseUrl + this.configService.url.nhanvien.exportBySheet, search, { observe: 'response', responseType: 'arraybuffer' });
  }

  download(): Observable<any> {
    return this.http.post(this.baseUrl + this.configService.url.nhanvien.download, null, { observe: 'response', responseType: 'arraybuffer' });
  }

  exportWithType(id: number, type: string): Observable<any> {
    return this.http.get(this.baseUrl + this.configService.url.nhanvien.exportById + '/' + id + '/' + type, { observe: 'response', responseType: 'arraybuffer' });
  }

  /**
   * Get predefined report file by type. Backend endpoint: GET /report/{type}
   */
  getReport(type: number): Observable<any> {
    return this.http.get(this.baseUrl + this.configService.url.nhanvien.report + '/' + type, { observe: 'response', responseType: 'arraybuffer' });
  }

  getListSelectNhanVien(): Observable<NhanVienShortModel[]> {
    const headers = this.getHeaders(true);
    return this.http.get<NhanVienShortModel[]>(this.baseUrl + this.configService.url.nhanvien.getListSelectNhanVien, { headers })
      .pipe(catchError(this.handleError('getListSelectNhanVien', new Array<NhanVienShortModel>())));
  }

  getListWarning(): Observable<NhanVienWarningModel[]> {
    const headers = this.getHeaders(true);
    return this.http.get<NhanVienWarningModel[]>(this.baseUrl + this.configService.url.nhanvien.getListWarning, { headers })
      .pipe(catchError(this.handleError('getListSelectNhanVien', new Array<NhanVienWarningModel>())));
  }

  exportPayrollSummary(summary: SummaryModel): Observable<any> {
    return this.http.post(this.baseUrl + this.configService.url.nhanvien.payrollSummary, summary, { observe: 'response', responseType: 'arraybuffer' });
  }

  import(model: any): Observable<any> {
    const headers = this.getHeaders(true);
    return this.http.post<ImportNhanVienModel>(this.baseUrl + this.configService.url.nhanvien.import, model, { headers });
  }

  saveImport(model: ImportNhanVienModel): Observable<any> {
    const headers = this.getHeaders(true);
    return this.http.post<any>(this.baseUrl + this.configService.url.nhanvien.saveImport, model, { headers })
      .pipe(catchError(this.handleError('saveImportNhanVien', new ResponseData<any>())));
  }

  getTemplateImport(): Observable<any> {
    return this.http.get(this.baseUrl + this.configService.url.nhanvien.getTemplate, { observe: 'response', responseType: 'arraybuffer' });
  }

}
